import { AnimatePresence, motion } from 'framer-motion';
import {
  Bath,
  BedDouble,
  Building,
  DollarSign,
  Home,
  Image as ImageIcon,
  MapPin,
  Ruler,
  Tag,
  X,
} from 'lucide-react';
import React, { useMemo } from 'react';

interface PayloadPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  payload: any | null;
}

const REQUIRED_FIELDS = [
  'purpose',
  'cityId',
  'areaId',
  'bed',
  'bath',
  'size',
  'sizeUnit',
  'price',
  'imageIds',
];

// Define types for better type safety
interface Field {
  key: string;
  label: string;
  icon: React.ElementType;
  format?: (value: any) => string;
  dependsOn?: string;
}

interface FieldGroup {
  group: string;
  fields: Field[];
}

// Apply the explicit type to the config array
const FieldConfig: FieldGroup[] = [
  {
    group: 'Basic Information',
    fields: [
      { key: 'purpose', label: 'Purpose', icon: Tag },
      { key: 'price', label: 'Price', icon: DollarSign, format: (v: number) => `PKR ${v.toLocaleString()}` },
    ],
  },
  {
    group: 'Property Details',
    fields: [
      { key: 'bed', label: 'Bedrooms', icon: BedDouble },
      { key: 'bath', label: 'Bathrooms', icon: Bath },
      { key: 'size', label: 'Size', icon: Ruler, dependsOn: 'sizeUnit' },
      { key: 'sizeUnit', label: 'Unit', icon: Home },
    ],
  },
  {
    group: 'Location',
    fields: [
      { key: 'city', label: 'City', icon: Building },
      { key: 'area', label: 'Area', icon: MapPin },
    ],
  },
  {
    group: 'Media',
    fields: [{ key: 'imageIds', label: 'Images', icon: ImageIcon, format: (v: any) => `${v?.length || 0} uploaded` }],
  },
];

const FieldItem = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: any }) => {
  const hasValue = value !== undefined && value !== null && value !== '' && (!Array.isArray(value) || value.length > 0);

  return (
      <div className={`field-item ${hasValue ? 'completed' : 'pending'}`}>
        <div className="field-icon">{icon}</div>
        <div className="field-details">
          <span className="field-label">{label}</span>
          <span className="field-value">{hasValue ? value : 'Not provided yet'}</span>
        </div>
      </div>
  );
};

export default function PayloadPreviewModal({ isOpen, onClose, payload }: PayloadPreviewModalProps) {
  const { completedCount, progress } = useMemo(() => {
    if (!payload) return { completedCount: 0, progress: 0 };
    const count = REQUIRED_FIELDS.reduce((acc, key) => {
      const value = payload[key];
      const isPresent = value !== undefined && value !== null && value !== '' && (!Array.isArray(value) || value.length > 0);
      return acc + (isPresent ? 1 : 0);
    }, 0);
    return {
      completedCount: count,
      progress: (count / REQUIRED_FIELDS.length) * 100,
    };
  }, [payload]);

  return (
      <AnimatePresence>
        {isOpen && (
            <motion.div
                className="payload-preview-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            >
              <motion.div
                  className="payload-preview-modal"
                  initial={{ x: '100%' }}
                  animate={{ x: '0%' }}
                  exit={{ x: '100%' }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  onClick={(e) => e.stopPropagation()}
              >
                <div className="preview-header">
                  <h2>Listing Preview</h2>
                  <p>Live view of the data being collected.</p>
                  <button className="close-button" onClick={onClose}>
                    <X size={24} />
                  </button>
                </div>

                <div className="progress-bar-container">
                  <div className="progress-bar-labels">
                    <span>Progress</span>
                    <span>{completedCount} / {REQUIRED_FIELDS.length} Fields</span>
                  </div>
                  <div className="progress-bar-track">
                    <motion.div
                        className="progress-bar-fill"
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.5, ease: 'easeInOut' }}
                    />
                  </div>
                </div>

                <div className="preview-content">
                  {payload ? (
                      <div className="fields-list">
                        {FieldConfig.map(({ group, fields }) => (
                            <div key={group} className="field-group">
                              <h3>{group}</h3>
                              {fields.map(({ key, label, icon: Icon, format, dependsOn }) => {
                                let value = payload[key];
                                if (dependsOn && payload[key]) {
                                  value = `${payload[key]} ${payload[dependsOn] || ''}`.trim();
                                }
                                if (format && value) {
                                  value = format(value);
                                }
                                return <FieldItem key={key} icon={<Icon size={20} />} label={label} value={value} />;
                              })}
                            </div>
                        ))}
                      </div>
                  ) : (
                      <div className="empty-state">
                        <p>Start the listing process to see data here.</p>
                      </div>
                  )}
                </div>

                <div className="preview-footer">
                  <p>This is a preview and not the final listing.</p>
                </div>
              </motion.div>
            </motion.div>
        )}
      </AnimatePresence>
  );
}